import { Outlet, NavLink, Link, useLocation } from 'react-router-dom'
import { useState, useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Menu, X, Activity, BookOpen, Wallet, Home as HomeIcon, DollarSign, ChevronDown } from 'lucide-react'
import { useBitcoinPrice, useMempoolData, fetchBitcoinPriceSnapshot, fetchMempoolSnapshot } from '../lib/useBlockchain'

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const location = useLocation()
  const queryClient = useQueryClient()
  const { price: btcPrice } = useBitcoinPrice()
  const { data: mempoolData, error: mempoolError } = useMempoolData()

  const navLinks = [
    { to: '/', label: 'Home', icon: HomeIcon },
    { to: '/live', label: 'Live', icon: Activity },
    {
      label: 'Learn',
      icon: BookOpen,
      children: [
        { to: '/markets', label: 'Analytics & Metrics' },
        { to: '/learn', label: 'Technology' },
      ],
    },
    {
      label: 'Finance',
      icon: DollarSign,
      children: [
        { to: '/power-law', label: 'Power Law Model' },
        { to: '/treasuries', label: 'Corporate Treasuries' },
        { to: '/microstrategy', label: 'Strategy (MSTR)' },
      ],
    },
    { to: '/wallets', label: 'Wallets', icon: Wallet },
  ]

  const handleDropdownKeyDown = (event, label) => {
    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      event.preventDefault()
      setOpenDropdown(label)
    }
    if (event.key === 'Escape') {
      setOpenDropdown(null)
    }
  }

  const renderDropdown = (link) => (
    <div
      key={link.label}
      className="relative"
      onMouseEnter={() => setOpenDropdown(link.label)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500"
        aria-haspopup="true"
        aria-expanded={openDropdown === link.label}
        onKeyDown={(e) => handleDropdownKeyDown(e, link.label)}
      >
        <link.icon size={16} />
        {link.label}
        <ChevronDown size={14} className={`transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
      </button>
      {openDropdown === link.label && (
        <div
          className="absolute left-0 top-full mt-0 w-56 rounded-xl border border-gray-800 bg-void-400 shadow-xl p-2 pt-1 z-50"
          onMouseEnter={() => setOpenDropdown(link.label)}
          onMouseLeave={() => setOpenDropdown(null)}
        >
          {link.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              onMouseEnter={prefetchCore}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'text-btc-500 bg-btc-500/10' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500`
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )

  const prefetchCore = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: ['price'],
      queryFn: () => fetchBitcoinPriceSnapshot(),
    })
    queryClient.prefetchQuery({
      queryKey: ['mempool'],
      queryFn: () => fetchMempoolSnapshot(),
    })
  }, [queryClient])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.pathname])

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-void-500/90 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-btc-500 to-yellow-500 rounded-xl flex items-center justify-center font-tech font-bold text-xl text-void-500">
              ₿
            </div>
            <span className="font-tech text-lg font-semibold tracking-wide">
              BTC<span className="text-btc-500">EXPLORER</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) =>
              link.children ? (
                renderDropdown(link)
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onMouseEnter={prefetchCore}
                  onFocus={prefetchCore}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? 'text-btc-500' : 'text-gray-400 hover:text-white'
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500`
                  }
                >
                  <link.icon size={16} />
                  {link.label}
                </NavLink>
              )
            )}
          </div>

          {/* Live Status */}
          <div className="hidden md:flex items-center gap-6">
            {mempoolData.blockHeight && (
              <div className="text-right">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Block</div>
                <div className="font-tech text-sm text-btc-500">{mempoolData.blockHeight.toLocaleString()}</div>
              </div>
            )}
            {btcPrice && (
              <div className="text-right">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Price</div>
                <div className="font-tech text-sm text-green-400">${btcPrice.toLocaleString()}</div>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 rounded-full">
              <div className="live-dot" />
              <span className="text-xs text-green-400 uppercase tracking-wider">Mainnet</span>
            </div>
            {mempoolError && (
              <span className="text-[10px] text-yellow-400 uppercase tracking-wider">Data delayed</span>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden p-2 text-gray-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500"
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-800">
            <div className="flex flex-col gap-2 pt-4">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 px-4 py-2 text-gray-300 uppercase tracking-wide text-xs">
                      <link.icon size={16} />
                      {link.label}
                    </div>
                    {link.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-6 py-3 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-btc-500/10 text-btc-500'
                              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                          } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-btc-500/10 text-btc-500'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-btc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-void-500`
                    }
                  >
                    <link.icon size={18} />
                    {link.label}
                  </NavLink>
                )
              )}
            </div>
          </div>
        )}
      </nav>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:bg-white focus:text-void-500 focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Main Content */}
      <main id="main-content" className="relative z-10 pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-800 bg-void-400/50">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-btc-500 to-yellow-500 rounded-xl flex items-center justify-center font-tech font-bold text-lg text-void-500">
                  ₿
                </div>
                <span className="font-tech text-lg font-semibold">
                  BTC<span className="text-btc-500">EXPLORER</span>
                </span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed max-w-md">
                Real-time Bitcoin blockchain visualization with live network data. 
                Built with React, Three.js, and WebSocket connections to blockchain APIs.
              </p>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-4">Explore</h4>
              <div className="flex flex-col gap-2">
                <Link to="/live" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Live Network</Link>
                <Link to="/markets" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Analytics</Link>
                <Link to="/learn" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Learn</Link>
                <Link to="/power-law" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Power Law</Link>
                <Link to="/treasuries" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Treasuries</Link>
                <Link to="/microstrategy" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Strategy (MSTR)</Link>
                <Link to="/wallets" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Wallets Guide</Link>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-4">Resources</h4>
              <div className="flex flex-col gap-2">
                  <a href="https://bitcoin.org/bitcoin.pdf" target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Whitepaper</a>
                  <a href="https://mempool.space" target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Mempool.space</a>
                  <a href="https://github.com/bitcoin/bips" target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Bitcoin BIPs</a>
                  <a href="https://developer.bitcoin.org" target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Developer Docs</a>
              </div>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-4">Legal</h4>
              <div className="flex flex-col gap-2">
                <a href="/terms.html" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Terms of Service</a>
                <a href="/privacy.html" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Privacy Policy</a>
                <a href="/disclaimer.html" className="text-sm text-gray-400 hover:text-btc-500 transition-colors">Disclaimer</a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-600">
              Data visualization for educational purposes. Not financial advice.
            </p>
            <a href="https://jacobkanfer.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-gray-400 hover:text-btc-500 transition-colors">
              <img src="/jk-logo.svg" alt="Jacob Kanfer" className="w-4 h-4" />
              Built by Jacob Kanfer
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
